/**
 * Figures are cached in the browser for a year, and the redesign does not change
 * a single URL.
 *
 * `/svg/` answers `Cache-Control: public, max-age=31536000, immutable`, which is
 * right — a figure for given parameters is the same picture forever, and it is
 * fetched on every question. But the picture those parameters DRAW changed when
 * the generators were redrawn, and the 7,808 figure URLs live inside content
 * files. A learner who has met a clock before would keep the old clock until next
 * year, and a session would mix two drawing styles in one sitting.
 *
 * So the version travels with the request. `_v` is already a cache-busting
 * parameter the endpoint keeps in its cache key and drops before calling a
 * generator, so nothing downstream has to learn about this.
 *
 * **Bump this whenever a generator's output changes** — that is the whole
 * contract. It is a date rather than a counter so a stale value is obvious.
 */
export const FIGURE_VERSION = "20261101b";

/** Matches the `src` of a generated figure, in either HTML escaping. */
const FIGURE_SRC = /(src\s*=\s*")(\/svg\/[^"]*)(")/gi;

/**
 * Stamps the version onto every `/svg/` src in a block of content HTML.
 *
 * Done here rather than in the content itself: the alternative is rewriting
 * thousands of exercise and theory files on every design change, and a figure's
 * URL is also what identifies it in an approval diff.
 *
 * Only `/svg/` is touched. A photograph under `/images/` is a file that really
 * does not change, and a raster with a query string busts nothing.
 */
export function versionFigures(html: string): string {
  return html.replace(FIGURE_SRC, (_full, before: string, url: string, after: string) => {
    if (url.includes("_v=")) return `${before}${url}${after}`;
    // `&amp;` where the document already escapes its entities, so the stamped URL
    // stays valid HTML rather than becoming valid-looking HTML.
    const separator = url.includes("?") ? "&amp;" : "?";
    return `${before}${url}${separator}_v=${FIGURE_VERSION}${after}`;
  });
}
